import axios from "axios";
import { LAYER_ZINDEX } from "constants/Constants";
import { MATRIX_SET } from "constants/GeoService";
import WMTSCapabilities from "ol/format/WMTSCapabilities";
import TileLayer from "ol/layer/Tile";
import WMTS, { optionsFromCapabilities } from "ol/source/WMTS";
import { mapStore } from "stores/Map";
import { useOpenLayers } from "./useOpenLayers";

export const useWMTS = () => {
  const { map } = mapStore();
  const { baseWMTSLayer, targetWMTSLayer, setBaseWMTSLayer, setTargetWMTSLayer } = mapStore();
  const { removeWMTSLayerEvent } = useOpenLayers();

  const fetchCapabilities = async (url: string) => {
    try {
      const response = await axios.get<string>(`${url}?request=GetCapabilities`);
      return response.data;
    } catch (error) {
      console.error("WMTS Capabilities Fetch Error:", error);
      return null;
    }
  };

  const removeAllWMTS = () => {
    if (baseWMTSLayer) {
      map?.removeLayer(baseWMTSLayer);
      setBaseWMTSLayer(null);
    }
    if (targetWMTSLayer) {
      map?.removeLayer(targetWMTSLayer);
      setTargetWMTSLayer(null);
    }
    removeWMTSLayerEvent();
  };

  const findMatrixSet = (
    layer: { TileMatrixSetLink: Array<{ TileMatrixSet: string }> },
    matrixSet: string
  ): string | undefined => {
    return layer.TileMatrixSetLink.find((link) => link.TileMatrixSet === matrixSet)?.TileMatrixSet;
  };

  const parseWMTSCapabilities = (capabilities: string): WMTS[] => {
    const parser = new WMTSCapabilities();
    const configResult = parser.read(capabilities);

    if (!configResult.Contents || !configResult.Contents.Layer) return [];

    return configResult.Contents.Layer.map((layer: any) => {
      const selectedMatrixSet =
        findMatrixSet(layer, "EPSG:900913x2") ??
        findMatrixSet(layer, "EPSG:900913") ??
        findMatrixSet(layer, MATRIX_SET.EPSG_900913) ??
        layer.TileMatrixSetLink[0]?.TileMatrixSet;

      const options = optionsFromCapabilities(configResult, {
        layer: layer.Identifier,
        matrixSet: selectedMatrixSet,
      });

      return options ? new WMTS({ ...options, wrapX: true }) : null;
    }).filter((src: WMTS | null): src is WMTS => !!src);
  };

  const addWMTS = async (url: string, layerName: string, type: "base" | "target") => {
    try {
      removeAllWMTS();

      const capabilitiesXml = await fetchCapabilities(url);
      if (!capabilitiesXml) return;

      const wmtsSources = parseWMTSCapabilities(capabilitiesXml);
      
      const selectedSource = layerName
        ? wmtsSources.find((source) => source.getLayer() === layerName)
        : wmtsSources[0];

      if (!selectedSource) {
        console.error(`Layer '${layerName}' not found in capabilities`);
        return;
      }

      const layer = new TileLayer({ source: selectedSource });

      if (type === "base") {
        layer.setZIndex(LAYER_ZINDEX.BASE_WMTS);
        setBaseWMTSLayer(layer);
      } else {
        layer.setZIndex(LAYER_ZINDEX.TARGET_WMTS);
        setTargetWMTSLayer(layer);
      }

      map?.addLayer(layer);
    } catch (error) {
      console.error("Add WMTS Error:", error);
    }
  };

  return {
    addWMTS,
    removeAllWMTS,
  };
};